import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { MessageSquareQuote, Plus, Save, Trash2, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../../components/ui/dialog";
import { api } from "../../lib/api";

/**
 * Phase A1 — Admin CRUD for the Home social-proof testimonials.
 *
 * Route: `/admin/testimonios`.
 *
 * Table view + modal form. The 5 wilayas dropdown matches the backend
 * `WilayaType` Literal (Ausserd / Smara / Dajla / El Aaiún / Bojador).
 * Quote length is validated 20–180 chars client-side (mirrors backend
 * Pydantic constraints).
 */

const WILAYAS = ["Ausserd", "Smara", "Dajla", "El Aaiún", "Bojador"];

const EMPTY_FORM = {
  author_name: "",
  wilaya: "Ausserd",
  quote: "",
  position: 1,
  is_active: true,
};

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/testimonials");
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error("No se pudieron cargar los testimonios");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm({
      ...EMPTY_FORM,
      position: (items.length || 0) + 1,
    });
    setModalOpen(true);
  };

  const openEdit = (t) => {
    setEditingId(t.id);
    setForm({
      author_name: t.author_name || "",
      wilaya: t.wilaya || "Ausserd",
      quote: t.quote || "",
      position: typeof t.position === "number" ? t.position : 1,
      is_active: t.is_active !== false,
    });
    setModalOpen(true);
  };

  const close = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
  };

  const validate = () => {
    if (!form.author_name.trim()) return "El nombre del autor es obligatorio";
    if (!WILAYAS.includes(form.wilaya)) return "Wilaya no válida";
    const q = form.quote.trim();
    if (q.length < 20) return "La cita debe tener al menos 20 caracteres";
    if (q.length > 180) return "La cita no puede superar 180 caracteres";
    if (!Number.isFinite(form.position)) return "Posición no válida";
    return null;
  };

  const save = async () => {
    const err = validate();
    if (err) {
      toast.error(err);
      return;
    }
    setSaving(true);
    try {
      const body = {
        author_name: form.author_name.trim(),
        wilaya: form.wilaya,
        quote: form.quote.trim(),
        position: Number(form.position) || 1,
        is_active: !!form.is_active,
      };
      if (editingId) {
        await api.patch(`/admin/testimonials/${editingId}`, body);
        toast.success("Testimonio actualizado");
      } else {
        await api.post("/admin/testimonials", body);
        toast.success("Testimonio creado");
      }
      close();
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const del = async (t) => {
    if (
      !window.confirm(
        `¿Eliminar el testimonio de ${t.author_name} (${t.wilaya})? Esta acción no se puede deshacer.`,
      )
    )
      return;
    try {
      await api.delete(`/admin/testimonials/${t.id}`);
      toast.success("Testimonio eliminado");
      await load();
    } catch (e) {
      toast.error("Error al eliminar");
    }
  };

  const toggleActive = async (t, next) => {
    try {
      await api.patch(`/admin/testimonials/${t.id}`, { is_active: next });
      setItems((prev) =>
        prev.map((x) => (x.id === t.id ? { ...x, is_active: next } : x)),
      );
    } catch (e) {
      toast.error("No se pudo actualizar el estado");
    }
  };

  const rows = useMemo(
    () =>
      [...items].sort(
        (a, b) =>
          (a.position ?? 999) - (b.position ?? 999) ||
          (a.author_name || "").localeCompare(b.author_name || ""),
      ),
    [items],
  );

  return (
    <div data-testid="admin-testimonials" className="space-y-4 max-w-5xl">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <MessageSquareQuote className="h-5 w-5 text-[#00953B]" />
          <h1 className="text-3xl font-black tracking-tight">Testimonios</h1>
          <span className="text-sm text-neutral-500">({items.length})</span>
        </div>
        <Button
          onClick={openCreate}
          data-testid="admin-testimonials-new-btn"
          className="gap-2 rounded-full bg-[#00953B] hover:bg-[#007a30] text-white"
        >
          <Plus className="h-4 w-4" /> Nuevo testimonio
        </Button>
      </div>

      <div className="rounded-2xl border bg-white overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-neutral-500">Cargando…</div>
        ) : rows.length === 0 ? (
          <div className="p-6 text-sm text-neutral-500">
            Aún no hay testimonios. Crea el primero con el botón de arriba.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 text-neutral-600 text-xs uppercase">
                <tr>
                  <th className="text-left px-3 py-2 w-16">Pos.</th>
                  <th className="text-left px-3 py-2">Autor</th>
                  <th className="text-left px-3 py-2">Wilaya</th>
                  <th className="text-left px-3 py-2">Cita</th>
                  <th className="text-left px-3 py-2 w-24">Activo</th>
                  <th className="text-right px-3 py-2 w-32">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {rows.map((t) => (
                  <tr
                    key={t.id}
                    data-testid={`admin-testimonial-row-${t.id}`}
                    className="hover:bg-neutral-50/60"
                  >
                    <td className="px-3 py-2 font-semibold text-neutral-700">
                      {t.position}
                    </td>
                    <td className="px-3 py-2 font-medium text-neutral-900">
                      {t.author_name}
                    </td>
                    <td className="px-3 py-2 text-neutral-600">{t.wilaya}</td>
                    <td className="px-3 py-2 text-neutral-600 max-w-md">
                      <div className="line-clamp-2">{t.quote}</div>
                    </td>
                    <td className="px-3 py-2">
                      <Switch
                        checked={t.is_active !== false}
                        onCheckedChange={(v) => toggleActive(t, v)}
                        data-testid={`admin-testimonial-active-${t.id}`}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openEdit(t)}
                          data-testid={`admin-testimonial-edit-${t.id}`}
                        >
                          Editar
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => del(t)}
                          data-testid={`admin-testimonial-delete-${t.id}`}
                          className="text-red-600 hover:bg-red-50 hover:text-red-700 border-red-200"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={modalOpen} onOpenChange={(v) => (v ? null : close())}>
        <DialogContent
          className="max-w-lg"
          data-testid="admin-testimonial-modal"
        >
          <DialogHeader>
            <DialogTitle>
              {editingId ? "Editar testimonio" : "Nuevo testimonio"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-3">
            <div>
              <Label htmlFor="tm-author">Nombre del autor</Label>
              <Input
                id="tm-author"
                value={form.author_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, author_name: e.target.value }))
                }
                placeholder="Ej. Fatimatu"
                data-testid="admin-testimonial-input-author"
                maxLength={80}
              />
            </div>

            <div>
              <Label>Wilaya</Label>
              <Select
                value={form.wilaya}
                onValueChange={(v) => setForm((f) => ({ ...f, wilaya: v }))}
              >
                <SelectTrigger data-testid="admin-testimonial-input-wilaya">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {WILAYAS.map((w) => (
                    <SelectItem key={w} value={w}>
                      {w}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="tm-quote">Cita (20–180 caracteres)</Label>
              <Textarea
                id="tm-quote"
                value={form.quote}
                onChange={(e) =>
                  setForm((f) => ({ ...f, quote: e.target.value }))
                }
                rows={3}
                placeholder="Ej. Mi hermano recibió el paquete en 8 días…"
                data-testid="admin-testimonial-input-quote"
                maxLength={180}
              />
              <div className="text-xs text-neutral-500 mt-1 text-right">
                {form.quote.trim().length} / 180
              </div>
            </div>

            <div className="flex items-end gap-3">
              <div className="flex-1">
                <Label htmlFor="tm-position">Posición</Label>
                <Input
                  id="tm-position"
                  type="number"
                  min={1}
                  max={99}
                  value={form.position}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      position: parseInt(e.target.value, 10) || 1,
                    }))
                  }
                  data-testid="admin-testimonial-input-position"
                />
              </div>
              <label className="flex items-center gap-2 text-sm pb-2">
                <Switch
                  checked={form.is_active}
                  onCheckedChange={(v) =>
                    setForm((f) => ({ ...f, is_active: v }))
                  }
                  data-testid="admin-testimonial-input-active"
                />
                Activo
              </label>
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={close}
              data-testid="admin-testimonial-cancel-btn"
            >
              <X className="h-4 w-4 mr-1" /> Cancelar
            </Button>
            <Button
              onClick={save}
              disabled={saving}
              data-testid="admin-testimonial-save-btn"
              className="bg-[#00953B] hover:bg-[#007a30] text-white"
            >
              <Save className="h-4 w-4 mr-1" />
              {saving ? "Guardando…" : editingId ? "Guardar cambios" : "Crear"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
